import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import CheckListRowComponent from './CheckListRowComponent';
import { Grid } from '@mui/material';
import { Card, IconButton, TextField, Typography } from '@totalsoft/rocket-ui';
import PaginationWrapper from 'components/common/PaginationWrapper';
import Search from '@mui/icons-material/Search';
import Close from '@mui/icons-material/Close';
import { CardButton } from '../ChecklistStyles';

const CheckListComponent = ({
  t,
  checkList,
  onPropertyChange,
  onSave,
  onAdd,
  onDelete,
  userId,
  saving,
  isDirty,
  isValid,
  documentChecklistWrite,
  readOnlyFull,
  checklists,
  checkListLoaded,
  toggleItemDeletion,
  getItemsDirtyStatus,
  onFilterValueChanged,
  fullTextFilter,
  resetFilters,
  rowsPerPageOptions,
  onChangeRowsPerPage,
  onChangePage,
  onRefresh,
  paginationAndFilterObj,
  itemsToDelete,
  filteredCheckList,
  addDeleteApprovalConditions
}) => {
  const getItemsToBeDeleted = () => {
    if (!checkList) return { status: false, value: 0 };
    var number = checkList.filter(item => Boolean(item?.document?.selectedForDeletion)).length;
    return { status: Boolean(number), value: number };
  };

  return (
    <React.Fragment>
      <Prompt when={getItemsDirtyStatus()} message={() => t('LeavingWithoutSaving')} />
      <Card
        id={'documentChecklist'}
        title={
          <Grid container direction={'row'}>
            <Grid item xs={6}>
              {t('Document.CheckList.Title')}
            </Grid>
            <Grid item xs={6}>
              <TextField
                id={'documentChecklist_filters_fullText'}
                fullWidth={true}
                name='fullTextFilter'
                disabled={!(filteredCheckList && filteredCheckList.length) && !fullTextFilter}
                value={fullTextFilter}
                onChange={value => onFilterValueChanged(value, true)}
                placeholder={t('Placeholders.ConditionSearch')}
                startAdornment={<Search />}
                endAdornment={
                  <IconButton
                    onClick={resetFilters}
                    disabled={!fullTextFilter}
                    id={'documentChecklist_filters_resetBtn'}
                    tooltip={t('Tooltips.ResetFilters')}
                    size={'tiny'}
                    color={'transparent'}
                  >
                    <Close />
                  </IconButton>
                }
              />
            </Grid>
          </Grid>
        }
        subheader={
          checkList && checkList.length > 0 ? null : (
            <Grid item xs={12}>
              <Typography variant={'subtitle2'}> {t('OfferAction.EmptyChecklistMessage')} </Typography>
            </Grid>
          )
        }
      >
        <Grid container direction={'column'}>
          <>
            {filteredCheckList &&
              filteredCheckList.map((item, idx) => {
                return (
                  <Grid item key={idx}>
                    <CheckListRowComponent
                      item={item.document}
                      validationInfo={item.validationInfo}
                      idx={idx}
                      onPropertyChange={onPropertyChange(idx)}
                      toggleItemDeletion={() => toggleItemDeletion(idx)}
                      userId={userId}
                      checklists={checklists}
                      documentChecklistWrite={documentChecklistWrite}
                      readOnlyFull={readOnlyFull}
                    />
                  </Grid>
                );
              })}
            <Grid item container xs={12} direction='row' spacing={1}>
              <Grid item xs={9} container alignContent='flex-start'>
                <>
                  <CardButton
                    id={'documentCheckList_addBtn'}
                    onClick={onAdd}
                    disabled={!checkListLoaded || !documentChecklistWrite || readOnlyFull || !addDeleteApprovalConditions}
                    variant={'contained'}
                  >
                    {t('Add')}
                  </CardButton>
                  {checkList && checkList.length > 0 && (
                    <>
                      <CardButton
                        id={'documentCheckList_deleteBtn'}
                        onClick={onDelete}
                        disabled={!(itemsToDelete > 0) || !documentChecklistWrite || readOnlyFull || !addDeleteApprovalConditions}
                        variant={'contained'}
                      >
                        {getItemsToBeDeleted().status ? `${t('Delete')} (${getItemsToBeDeleted().value})` : t('Delete')}
                      </CardButton>
                      <CardButton
                        id={'documentCheckList_saveBtn'}
                        onClick={onSave}
                        disabled={saving || !isDirty || !isValid || !documentChecklistWrite || readOnlyFull}
                        variant={'contained'}
                      >
                        {saving ? t('Saving') : t('Save')}
                      </CardButton>
                    </>
                  )}
                </>
              </Grid>
              <Grid item xs={3}>
                <PaginationWrapper
                  loading={false}
                  count={paginationAndFilterObj.totalCount}
                  pageSize={paginationAndFilterObj.pageSize}
                  page={paginationAndFilterObj.page}
                  rowsPerPageOptions={rowsPerPageOptions}
                  onRowsPerPageChange={onChangeRowsPerPage}
                  onPageChange={onChangePage}
                  onRefresh={onRefresh}
                />
              </Grid>
            </Grid>
          </>
        </Grid>
      </Card>
    </React.Fragment>
  );
};

CheckListComponent.propTypes = {
  checkList: PropTypes.array,
  filteredCheckList: PropTypes.array,
  onPropertyChange: PropTypes.func.isRequired,
  toggleItemDeletion: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  userId: PropTypes.number,
  documentChecklistWrite: PropTypes.bool,
  readOnlyFull: PropTypes.bool,
  t: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  isDirty: PropTypes.bool.isRequired,
  isValid: PropTypes.bool.isRequired,
  checklists: PropTypes.array.isRequired,
  checkListLoaded: PropTypes.bool.isRequired,
  getItemsDirtyStatus: PropTypes.func.isRequired,
  onChangeRowsPerPage: PropTypes.func.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  paginationAndFilterObj: PropTypes.object.isRequired,
  rowsPerPageOptions: PropTypes.array.isRequired,
  resetFilters: PropTypes.func.isRequired,
  itemsToDelete: PropTypes.number.isRequired,
  onSave: PropTypes.func.isRequired,
  onFilterValueChanged: PropTypes.func.isRequired,
  fullTextFilter: PropTypes.string,
  addDeleteApprovalConditions: PropTypes.bool
};

export default withTranslation()(CheckListComponent);
